import React from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { Input } from '../ui/Input/Input'
import { Button } from '../ui/Button/Button'
import { Alert } from '../ui/Alert/Alert'
import { OrgService } from '../../services/OrgService'
import { useAuthStore } from '../../stores/authStore'
import { Logo } from '../ui/Logo'

export function AcceptInvite() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = React.useMemo(() => (searchParams.get('token') || '').trim(), [searchParams])

  const [fullName, setFullName] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [resolved, setResolved] = React.useState<null | { status: 'valid'|'invalid'|'expired'|'accepted'; email?: string; role?: 'admin'|'member'; organization?: { id: string; name: string } | null; userExists?: boolean; hasPassword?: boolean; }>(null)
  const [accepted, setAccepted] = React.useState(false)

  const svc = React.useMemo(() => new OrgService(), [])
  // No direct setUser, but we can use the setState method

  React.useEffect(() => {
    let isMounted = true
    async function run() {
      setError(null)
      setResolved(null)
      if (!token) return
      try {
        const data = await svc.resolveInvite(token)
        if (!isMounted) return
        setResolved(data)
      } catch (e) {
        if (!isMounted) return
        setError(e instanceof Error ? e.message : 'Failed to load invite')
      }
    }
    run()
    return () => { isMounted = false }
  }, [token, svc])

  const canSubmit = React.useMemo(() => {
    if (!resolved || resolved.status !== 'valid') return false
    if (!fullName.trim()) return false
    if (password.length < 8) return false
    if (password !== confirmPassword) return false
    return true
  }, [resolved, fullName, password, confirmPassword])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return setError('Missing invite token')
    if (!canSubmit) return
    try {
      setIsSubmitting(true)
      setError(null)
      const result = await svc.acceptInvite(token, fullName.trim(), password)
      
      if (result.autoLoggedIn && result.user) {
        // User is now logged in, set auth state and redirect
        useAuthStore.setState({ user: result.user })
        navigate('/dashboard')
      } else {
        // Show success state if auto-login failed
        setAccepted(true)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to accept invite')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Layout similar to AuthPage
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
            <Logo size="xl" className="justify-center mb-2" />
            <p className="text-sm text-muted-foreground mt-1">Artistic Intelligence</p>
          </Link>
        </div>

        <div className="bg-background rounded-2xl shadow-xl overflow-hidden border">
          <div className="p-6 md:p-8">
            <h2 className="text-xl font-semibold mb-2">Accept your invite</h2>
            {!token && (
              <Alert variant="error" className="mb-4">Invalid invite link. Please check the URL.</Alert>
            )}
            {resolved && resolved.status === 'invalid' && (
              <Alert variant="error" className="mb-4">This invite link is invalid.</Alert>
            )}
            {resolved && resolved.status === 'expired' && (
              <Alert variant="error" className="mb-4">This invite has expired. Ask the inviter to send a new one.</Alert>
            )}
            {resolved && resolved.status === 'accepted' && (
              <Alert variant="info" className="mb-4">This invite was already accepted. You can sign in now.</Alert>
            )}
            {error && (
              <Alert variant="error" className="mb-4">{error}</Alert>
            )}

            {accepted ? (
              <div className="space-y-4">
                <Alert variant="success">You're in! Your account has been set up and added to {resolved?.organization?.name || 'the organization'}. You can sign in now.</Alert>
                <div className="flex gap-3">
                  <Button variant="primary" className="flex-1" onClick={() => navigate('/auth')}>Go to sign in</Button>
                  <Button variant="ghost" className="flex-1" onClick={() => navigate('/')}>Back to home</Button>
                </div>
              </div>
            ) : (
              <>
                {resolved && resolved.status === 'valid' && (
                  <>
                    <div className="mb-4 text-sm text-muted-foreground">
                      <p>You've been invited to join <span className="font-medium text-foreground">{resolved.organization?.name || 'an organization'}</span>.</p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">What will happen next?</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {!resolved.userExists ? (
                          <>
                            <li>• Your account will be created with <span className="font-medium text-gray-900">{resolved.email}</span></li>
                            <li>• You'll be added as <span className="font-medium text-gray-900">{resolved.role}</span> to {resolved.organization?.name}</li>
                            <li>• You'll be automatically signed in to start working</li>
                          </>
                        ) : (
                          <>
                            <li>• Your account <span className="font-medium text-gray-900">{resolved.email}</span> will be added to the organization</li>
                            <li>• You'll have <span className="font-medium text-gray-900">{resolved.role}</span> access level</li>
                            <li>• You'll be automatically signed in to access your new workspace</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </>
                )}

                {resolved?.status === 'valid' && (
                  <form onSubmit={onSubmit} className="space-y-4">
                    <Input
                      label="Full name"
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                      placeholder="John Doe"
                      required
                    />
                    <Input
                      label="Password"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      minLength={8}
                      required
                      hint="Must be at least 8 characters"
                    />
                    <Input
                      label="Confirm password"
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      minLength={8}
                      required
                      error={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : undefined}
                    />

                    <Button type="submit" className="w-full" loading={isSubmitting} disabled={!canSubmit || isSubmitting || !token || !resolved || resolved.status !== 'valid'}>
                      Accept invite
                    </Button>

                    <p className="text-xs text-muted-foreground text-center mt-2">
                      By accepting, you agree to our{' '}
                      <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link>
                      {' '}and{' '}
                      <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>
                    </p>
                  </form>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-muted-foreground">
          Having trouble? Contact support.
        </div>
      </div>
    </div>
  )
}


